import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { CardContent } from '@/shared/components/card-content/card-content';
import { LayoutService } from '@/layout/service/layout.service';
import { iniciais } from '@/shared/utils/nome-utils';
import { FuncionarioService } from '@/core/api/sicom-api/sistema/funcionario/funcionario.service';
import { FuncionarioItemResponse } from '@/core/api/sicom-api/sistema/funcionario/funcionario.model';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';
import { MascaraUtil } from '@/shared/utils/mascara-util';
import { FuncionarioChave, FuncionarioDetalhe } from '../dialogs/funcionario-detalhe/funcionario-detalhe';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-funcionario-lista',
    standalone: true,
    imports: [
        FormsModule,
        ButtonModule,
        TableModule,
        TagModule,
        TooltipModule,
        InputTextModule,
        PaginatorModule,
        SelectEstado,
        TableViewSwitcherComponent,
        PessoaAvatarComponent,
        CardContainer,
        CardContent,
        CanDirective,
        FuncionarioDetalhe,
        FiltrosBar,
        FiltroCampo
    ],
    templateUrl: './funcionario-lista.html',
    styleUrl: './funcionario-lista.scss'
})
/**
 * Cadastro de funcionários (Configurações → Registro → Funcionários).
 *
 * <b>Não tem exclusão</b>, e é decisão de projeto: a saída da instituição é a data de afastamento,
 * dentro do próprio cadastro. Por isso também não existe role de excluir.
 *
 * A paginação é do servidor — a tabela tem ~500 linhas hoje, mas o filtro precisa alcançar quem não
 * está na página visível, e filtrar no cliente só filtraria essa.
 */
export class FuncionarioLista implements OnInit {
    protected readonly roleFuncionariosCriar = PERMISSOES.configuracoes.funcionarios.criar;
    protected readonly roleFuncionariosEditar = PERMISSOES.configuracoes.funcionarios.editar;

    private readonly service = inject(FuncionarioService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly permissionService = inject(PermissionService);
    private readonly layoutService = inject(LayoutService);

    /** Cards × tabela, como em Estruturas: a vista inicial vem da preferência do layout. */
    protected readonly currentView = signal<'card' | 'table'>(this.layoutService.getPresentationType());

    protected readonly carregando = signal(false);
    protected readonly itens = signal<FuncionarioItemResponse[]>([]);
    protected readonly total = signal(0);

    // ── Filtros: pendentes (bound) e aplicados (usados na busca) ───────────
    protected readonly filtroTexto = signal('');
    protected readonly filtroUf = signal('');
    private readonly filtroTextoAplicado = signal('');
    private readonly filtroUfAplicado = signal('');

    /** Regional não escolhe UF: o backend impõe a dele, e o select reflete isso em vez de mentir. */
    protected readonly ufTravada = computed(() => !this.permissionService.isCfm);

    protected readonly paginaAtual = signal(0);
    protected readonly itensPorPagina = signal(20);

    // ── Diálogo de detalhe ─────────────────────────────────────────────────
    protected readonly modalDetalhe = signal(false);
    protected readonly funcionarioSelecionado = signal<FuncionarioChave | null>(null);

    ngOnInit(): void {
        // Regional: o backend só devolve a UF dele — o filtro nasce nela em vez de dizer "todas as UFs".
        const ufOperador = this.ufOperador();
        if (ufOperador) {
            this.filtroUf.set(ufOperador);
            this.filtroUfAplicado.set(ufOperador);
        }
        this.carregar();
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.permissionService.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    protected carregar(): void {
        this.carregando.set(true);
        this.service
            .listar({
                q: this.filtroTextoAplicado(),
                uf: this.filtroUfAplicado(),
                page: this.paginaAtual(),
                size: this.itensPorPagina()
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (pagina) => {
                    this.itens.set(pagina.content ?? []);
                    this.total.set(pagina.totalElements ?? 0);
                    this.carregando.set(false);
                },
                error: () => {
                    this.carregando.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar funcionários' });
                }
            });
    }

    protected aplicarFiltros(): void {
        this.filtroTextoAplicado.set(this.filtroTexto().trim());
        this.filtroUfAplicado.set(this.filtroUf().trim().toUpperCase());
        // Volta para a primeira pagina: com o filtro novo, a pagina 4 anterior pode nem existir.
        this.paginaAtual.set(0);
        this.carregar();
    }

    protected limparFiltros(): void {
        this.filtroTexto.set('');
        // Limpar não destrava a UF do regional: ela volta para a dele, não para "todas".
        this.filtroUf.set(this.ufOperador());
        this.aplicarFiltros();
    }

    protected onPaginacaoMudar(event: PaginatorState): void {
        this.paginaAtual.set(event.page ?? 0);
        this.itensPorPagina.set(event.rows ?? 20);
        this.carregar();
    }

    protected onEstadoChange(uf: string): void {
        this.filtroUf.set((uf ?? '').toUpperCase());
    }

    protected novoFuncionario(): void {
        this.funcionarioSelecionado.set(null);
        this.modalDetalhe.set(true);
    }

    protected editarFuncionario(f: FuncionarioItemResponse): void {
        this.funcionarioSelecionado.set({ uf: f.uf, idSolicitante: f.idSolicitante });
        this.modalDetalhe.set(true);
    }

    protected onSalvo(): void {
        this.modalDetalhe.set(false);
        this.carregar();
    }

    /** A marca vem resolvida da consulta — a lista não busca o AGR linha a linha. */
    protected ehAgenteAgr(f: FuncionarioItemResponse): boolean {
        return f.inAgenteAgr === 'S';
    }

    protected ehAfastado(f: FuncionarioItemResponse): boolean {
        return !!f.dtAfastamento;
    }

    protected cpfFormatado(cpf: string | null): string {
        return cpf ? MascaraUtil.cpfCnpj(cpf) : '—';
    }

    /** Iniciais do avatar do card. Não há foto de funcionário: o avatar é só a letra. */
    protected iniciaisDe(f: FuncionarioItemResponse): string {
        return iniciais(f.nome);
    }

    protected situacaoSeverity(f: FuncionarioItemResponse): 'success' | 'warn' {
        return this.ehAfastado(f) ? 'warn' : 'success';
    }

    protected situacaoLabel(f: FuncionarioItemResponse): string {
        return this.ehAfastado(f) ? 'Afastado' : 'Ativo';
    }

    /** `'2026-03-01'` → `'01/03/2026'`. Split, e não `Date`, para o fuso não mover o dia. */
    protected dataFormatada(iso: string | null): string {
        if (!iso) return '—';
        const [ano, mes, dia] = iso.split('-');
        return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
    }

    protected get primeiroItem(): number {
        return this.paginaAtual() * this.itensPorPagina();
    }

    protected get temFiltrosAtivos(): boolean {
        return !!this.filtroTexto() || !!this.filtroUf();
    }
}
